import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "./cn";

export type ButtonVariant = "primary" | "cta" | "ghost" | "outline";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-brand-fill text-white hover:bg-brand-fill-hover active:bg-brand-fill-active",
  cta: "bg-cta text-white hover:bg-cta-hover active:bg-cta-active shadow-cta",
  ghost: "bg-transparent text-ink hover:bg-surface-muted",
  outline: "bg-transparent text-ink border border-border hover:bg-surface-muted",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-9 px-3 text-button-sm rounded-md",
  md: "h-11 px-4 text-button rounded-md",
  lg: "h-12 px-6 text-button rounded-lg",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center gap-2 font-semibold transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    />
  ),
);
Button.displayName = "Button";
